// src/app/shared/name-change-requests/Filters.tsx
"use client";

import { FilterDrawerView } from "@core/components/controlled-table/table-filter";
import { useState } from "react";
import { useMedia } from "react-use";
import { PiFunnel, PiMagnifyingGlassBold } from "react-icons/pi";
import { Button, Flex, Input } from "rizzui";
import ToggleColumns from "@core/components/table-utils/toggle-columns";
import type { Table as ReactTableType } from "@tanstack/react-table";

interface TableToolbarProps<T extends Record<string, any>> {
  table: ReactTableType<T>;
}

export default function Filters<TData extends Record<string, any>>({
  table,
}: TableToolbarProps<TData>) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const isLarge = useMedia("(min-width: 1920px)", false);

  return (
    <Flex align="center" justify="between" className="mb-4 gap-0">
      <Flex align="center" justify="between" className="flex-wrap  gap-2">
        {/* Search by Request ID */}
        <Input
          type="search"
          placeholder="Search by Request ID…"
          value={(table.getColumn("id")?.getFilterValue() as string) ?? ""}
          onClear={() => table.getColumn("id")?.setFilterValue(undefined)}
          onChange={(e) =>
            table.getColumn("id")?.setFilterValue(e.target.value)
          }
          inputClassName="h-9"
          clearable
          prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
        />

        {/* On large view, show inline status filter */}
        {isLarge && showFilters && <FilterElements table={table} />}
      </Flex>

      <Flex align="center" className="w-auto gap-2">
        <Button
          variant="outline"
          className="h-[34px] px-2"
          onClick={() =>
            isLarge ? setShowFilters((f) => !f) : setOpenDrawer((d) => !d)
          }
        >
          <PiFunnel className="me-1.5 h-4 w-4" />
          {isLarge && showFilters ? "Hide Filters" : "Filters"}
        </Button>

        {!isLarge && (
          <FilterDrawerView
            drawerTitle="Table Filters"
            isOpen={openDrawer}
            setOpenDrawer={setOpenDrawer}
          >
            <div className="grid grid-cols-1 gap-4">
              <FilterElements table={table} />
            </div>
          </FilterDrawerView>
        )}

        <ToggleColumns table={table} />
      </Flex>
    </Flex>
  );
}

function FilterElements<T extends Record<string, any>>({
  table,
}: TableToolbarProps<T>) {
  return (
    <>
      <select
        className="h-9 rounded border px-2"
        value={(table.getColumn("status")?.getFilterValue() as string) ?? ""}
        onChange={(e) =>
          table.getColumn("status")?.setFilterValue(e.target.value || undefined)
        }
      >
        <option value="">All Status</option>
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="rejected">Denied</option>
      </select>
    </>
  );
}
